/**
 * Plain-language vocabulary for the benchmark results.
 *
 * The reader is an AML operations lead or investigator, not a data scientist.
 * The evaluation is real model evaluation underneath — ROC AUC, bootstrap
 * intervals, per-feature discrimination — but none of that is how a compliance
 * team decides whether to change how alerts are triaged.
 *
 * So: the plain term is the LABEL, and the technical term is, at most, secondary
 * text beside it. The previous dashboard inverted this — headings read "Risk
 * Separation (ROC AUC)" and "Typology Consistency & Temporal Drift Audit", with
 * the translation hidden in a hover tooltip nobody opens. At rest the reader saw
 * only the jargon.
 *
 * Every technical figure still exists; it lives on the Evidence tab, where a
 * model validator expects to find it.
 */

import { TierCode } from '../../services/benchmarkService';

/** Priority tiers, most urgent first. Order is load-bearing for review policies. */
export const TIERS_BY_URGENCY: TierCode[] = ['P5', 'P4', 'P3', 'P2'];

export interface TierMeta {
  code: TierCode;
  /** What an investigator calls it. */
  label: string;
  color: string;
}

/**
 * Tier display, in one place.
 *
 * Bare `P5` / `P2` codes carry no meaning to anyone who has not memorised the
 * institution's scale, so the name leads and the code follows in brackets. The
 * colour map used to be duplicated between the migration chart and the alerts
 * table, where the two copies were free to drift apart.
 */
export const TIERS: Record<TierCode, TierMeta> = {
  P5: { code: 'P5', label: 'Urgent', color: '#EF4444' },
  P4: { code: 'P4', label: 'High', color: '#F97316' },
  P3: { code: 'P3', label: 'Medium', color: '#FBBF24' },
  P2: { code: 'P2', label: 'Low', color: '#0EA5E9' }
};

export const tierMeta = (code: string): TierMeta =>
  TIERS[code as TierCode] || { code: code as TierCode, label: code, color: '#94A3B8' };

/**
 * Tier colours as chart marks on a light surface. The `color` above is tuned for
 * navy; on white its yellow and sky drop under 3:1, so marks take these instead.
 */
const TIER_MARK_COLORS_LIGHT: Record<TierCode, string> = {
  P5: '#DC2626',
  P4: '#F97316',
  P3: '#B45309',
  P2: '#0284C7'
};

export const tierMarkColor = (code: string, theme: 'dark' | 'light'): string =>
  theme === 'light' ? TIER_MARK_COLORS_LIGHT[code as TierCode] ?? '#64748B' : tierMeta(code).color;

/** "Urgent (P5)" — name first, code for anyone who works in codes. */
export const tierLabel = (code: string): string => {
  const meta = TIERS[code as TierCode];
  return meta ? `${meta.label} (${meta.code})` : code;
};

/** "Urgent + High" — for naming a review policy by the tiers it works. */
export const tierSetLabel = (codes: string[]): string =>
  codes.map((c) => tierMeta(c).label).join(' + ');

/**
 * What the investigation actually found.
 *
 * `true_positive` / `false_positive` are evaluation terms for a prediction. The
 * reader cares about the case: was there something there or not. "True Crime"
 * (the previous label) also overstates it — a confirmed alert is escalated
 * suspicion, not an adjudicated crime.
 */
export const outcomeLabel = (outcomeClass: string): string => {
  if (outcomeClass === 'true_positive') return 'Confirmed';
  if (outcomeClass === 'false_positive') return 'No issue found';
  return 'Not reviewed';
};

export const outcomeColor = (outcomeClass: string): string => {
  if (outcomeClass === 'true_positive') return '#10B981';
  if (outcomeClass === 'false_positive') return '#94A3B8';
  return '#64748B';
};

/**
 * Why an alert was pushed up, phrased for a case file.
 *
 * Built from the structured signals the API returns. Anything unrecognised is
 * humanised from its key rather than dropped, so a new signal still reads as
 * something rather than vanishing from the explanation.
 */
export const signalPhrase = (code: string, value: number | boolean): string => {
  switch (code) {
    case 'high_risk_counterparty_count':
      return `${value} high-risk ${Number(value) === 1 ? 'counterparty' : 'counterparties'} in this customer's network`;
    case 'high_risk_account_flag':
      return 'Account is designated high-risk';
    case 'burst_flag':
      return 'Sudden spike in transaction activity';
    case 'structuring_window_flag':
      return 'Payments repeatedly just under the reporting threshold';
    case 'round_amount_flag':
      return 'Series of round-number transfers';
    default:
      return humanise(code);
  }
};

/**
 * Turns `kyc_tenure_days` into `Kyc Tenure Days` for anything unmapped.
 *
 * Lower-cases first, so an all-caps code like `NEWTYPE_09` reads as `Newtype 09`
 * rather than being left shouting — most unmapped keys arriving here are segment
 * or feature codes, which are upper-case by convention.
 */
export const humanise = (key: string): string =>
  String(key || '')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

/**
 * Risk signals by name rather than by column.
 *
 * Unmapped keys fall back to a humanised form; the raw key stays available for
 * the Evidence tab, where knowing the exact field matters.
 */
const FEATURE_NAMES: Record<string, string> = {
  high_risk_counterparty_count: 'High-risk counterparty connections',
  structuring_window_flag: 'Payments just under the reporting threshold',
  burst_flag: 'Sudden spike in transaction activity',
  round_amount_flag: 'Round-number transfers',
  account_type_risk_score: 'High-risk account type',
  peer_percentile_rank: 'Unusual compared with similar customers',
  kyc_tenure_days: 'How long the customer has been known',
  layer1_base_signals: 'Existing monitoring rules',
  layer2_customer_risk: 'Customer and counterparty risk',
  layer3_network_amplifiers: 'Network connections',
  layer4_recency_velocity: 'Recent activity and speed',
  priority_score: 'Overall priority score'
};

export const featureName = (key: string): string => FEATURE_NAMES[key] || humanise(key);

/** Scoring layers, named for what they look at. */
export const layerName = (key: string): string => FEATURE_NAMES[key] || humanise(key);

/**
 * Detection scenarios by name.
 *
 * The four codes below were hardcoded into a filter dropdown in the alerts
 * table, so a run using any other scenario showed an empty list. Now the list
 * comes from the run and this only supplies a nicer name where one is known.
 *
 * These are the institution's rule codes, not typologies: what a rule detects is
 * the approved mapping in Ontology → Your vocabulary, which an investigation run
 * carries in its manifest (`DimensionsResponse.scenarios`).
 */
const SCENARIO_NAMES: Record<string, string> = {
  HIRISK_03: 'High-risk countries and corridors',
  RAPID_02: 'Money moving straight through',
  STRUCT_01: 'Payments under the reporting threshold',
  VELOC_04: 'Sudden jump in transaction volume'
};

export const scenarioName = (code: string): string => SCENARIO_NAMES[code] || humanise(code);

/**
 * Definitions shown behind the info affordance next to each figure.
 *
 * These name the technical term too. An AML lead does not need it, but the model
 * validator sitting next to them does, and hiding the correspondence entirely
 * would make the two groups unable to talk about the same number.
 */
export const DEFINITIONS: Record<string, string> = {
  workloadSaved:
    'The share of alerts that would go unworked if your team only reviewed the tiers above the cut-off. Measured on alerts your investigators already closed, so the outcome of every one is known.',
  riskRetained:
    'Of the alerts investigators confirmed as genuine, the share that would still reach someone under this policy. The rest would be closed without review.',
  urgentHitRate:
    'Out of every 100 alerts in the urgent queue, how many turned out to be genuine. Compared against the same measure for the tier your current rules call urgent. (Precision at the top tier.)',
  topQueueHitRate:
    'If an investigator works only the top 10% of the ranked queue, the share of those alerts that were genuine. (Precision at 10%.)',
  sarConcentration:
    'Of all the SARs filed across this caseload, the share that the agent placed in the urgent queue.',
  rankingQuality:
    'How reliably the agent ranks genuine cases above innocent ones across the whole queue, from 0.5 (no better than chance) to 1.0 (perfect). Known to model validators as ROC AUC.',
  confidenceInterval:
    'The range the improvement is expected to fall in if the test were repeated on comparable data. A range entirely above zero means the improvement is unlikely to be chance.',
  rescued:
    'Alerts your current rules placed in a low tier, which the agent raised to a higher one, and which investigators went on to confirm or file a SAR on.',
  populationFrame:
    'Which alerts this test covers. "alerted_only" means it measures re-ordering within the alerts your monitoring already raises — it says nothing about activity that never triggered an alert.',
  stability:
    'Whether performance held up across separate time periods. A large gap between periods suggests the result may not carry forward.',
  lookahead:
    'Confirmation that each alert was scored using only information available at the time, so later knowledge of the outcome could not leak into the score.',
  scorerFingerprint:
    'A digest of the exact scoring configuration that was tested, so this result can be tied to a specific version later.'
};

/** Shown wherever a run recorded no figure. */
export const NOT_COMPUTED = 'Not computed for this run';

/* ------------------------------------------------------------------ *
 * Formatting
 *
 * The API returns raw numbers only. It used to return finished strings
 * ("44.9%", "2.6x increase in investigator hit rate"), which made the wording
 * impossible to adapt for the reader and put copy in a service layer.
 * ------------------------------------------------------------------ */

/** A fraction as a percentage, or an em dash when it was never computed. */
export const percent = (fraction: number | null | undefined, places = 1): string =>
  fraction === null || fraction === undefined || !Number.isFinite(fraction)
    ? '—'
    : `${(fraction * 100).toFixed(places)}%`;

export const count = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : value.toLocaleString();

export const multiplier = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? '—' : `${value.toFixed(1)}×`;

export const decimal = (value: number | null | undefined, places = 3): string =>
  value === null || value === undefined || !Number.isFinite(value) ? '—' : value.toFixed(places);

export const hasValue = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined && Number.isFinite(value);

/**
 * How much investigator time a workload reduction frees.
 *
 * Deliberately a function of a caller-supplied handling time rather than a
 * constant: the previous dashboard printed "~4.5 FTE" as fixed text, derived
 * from an unstated 45-minute assumption that the reader could neither see nor
 * change. Institutions differ, so the assumption is shown and editable on screen.
 */
export const MONTHLY_HOURS_PER_FTE = 160;

export const fteFromAlerts = (
  alertsAvoided: number | null | undefined,
  minutesPerAlert: number
): number | null => {
  if (!hasValue(alertsAvoided) || !Number.isFinite(minutesPerAlert) || minutesPerAlert <= 0) return null;
  return (alertsAvoided * minutesPerAlert) / 60 / MONTHLY_HOURS_PER_FTE;
};
